/**
 * feedback.ts — Dữ liệu + hàm thuần cho nút bong bóng "Góp ý" (feedback-bubble.tsx).
 */
import { i18n } from '@/lib/i18n'
import { MAX_IMAGES } from './feedback-images'
import { MAX_KEPT, pathOnly, type ApiFailure, type ClientError } from './client-diagnostics'

export type FeedbackType = 'bug' | 'idea' | 'feature'

export const MESSAGE_MAX = 4000
export const MESSAGE_MIN = 3

export const FEEDBACK_TYPE_OPTIONS: ReadonlyArray<{ value: FeedbackType; label: string; emoji: string; placeholder: string }> = [
  { value: 'bug', get label() { return i18n.t('sharedMisc:feedback.type.bug.label') }, emoji: '🐞', get placeholder() { return i18n.t('sharedMisc:feedback.type.bug.placeholder') } },
  { value: 'idea', get label() { return i18n.t('sharedMisc:feedback.type.idea.label') }, emoji: '💡', get placeholder() { return i18n.t('sharedMisc:feedback.type.idea.placeholder') } },
  { value: 'feature', get label() { return i18n.t('sharedMisc:feedback.type.feature.label') }, emoji: '✨', get placeholder() { return i18n.t('sharedMisc:feedback.type.feature.placeholder') } },
]

/** Tên trình duyệt + hệ điều hành ngắn gọn từ user-agent (chỉ để hiển thị cho người dùng biết sẽ gửi gì). */
export function browserLabel(ua: string): string {
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\/|Opera/.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\/|CriOS\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : i18n.t('sharedMisc:feedback.unknownBrowser')
  const os =
    /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : ''
  return os ? `${browser} · ${os}` : browser
}

export interface FeedbackEnv {
  pathname: string
  userAgent: string
  viewportW: number
  viewportH: number
}

export interface FeedbackPayload {
  type: FeedbackType
  message: string
  page: string
  withDiagnostics: boolean
  appVersion: string
  userAgent?: string
  viewport?: string
  errors?: ClientError[]
  apiFailures?: ApiFailure[]
  /** Ảnh chụp màn hình (data URL JPEG đã thu nhỏ) — người dùng tự chọn nên gửi bất kể ô thông tin kỹ thuật. */
  images?: string[]
}

/** Dựng body gửi lên máy chủ. Không tick "đính kèm thông tin kỹ thuật" → chỉ gửi loại + nội dung + trang đang mở. */
export function buildFeedbackPayload(
  input: { type: FeedbackType; message: string; withDiagnostics: boolean; images?: string[] },
  env: FeedbackEnv,
  diag: { errors: ClientError[]; apiFailures: ApiFailure[] },
  appVersion = '',
): FeedbackPayload {
  const base: FeedbackPayload = {
    type: input.type,
    message: input.message.trim().slice(0, MESSAGE_MAX),
    page: pathOnly(env.pathname),
    withDiagnostics: input.withDiagnostics,
    appVersion,
    ...(input.images?.length ? { images: input.images.slice(0, MAX_IMAGES) } : {}),
  }
  if (!input.withDiagnostics) return base
  return {
    ...base,
    userAgent: env.userAgent.slice(0, 300),
    viewport: `${env.viewportW}x${env.viewportH}`,
    errors: diag.errors.slice(-MAX_KEPT),
    apiFailures: diag.apiFailures.slice(-MAX_KEPT),
  }
}

export const canSendFeedback = (message: string): boolean => message.trim().length >= MESSAGE_MIN
